import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useAnalyticsProductsList from "@babackapp/private-api-client/client/use-analytics-products-list";
import useProductsSummaryAnalytics from "@babackapp/private-api-client/client/use-products-summary-analytics";
import { ProductAnalyticsItem } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { ScrollArea } from "@babackapp/ui/components/scroll-area";
import { useDashboardFilters } from "../lib/dashboard-filters";
import useFormattedTimeRange from "../lib/use-formatted-time-range";
import { ProductListItem } from "./product-list-item";
import { ProductSelectSearch } from "@/pages/(app)/home/components/product-select-search.tsx";
import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

const MAX_SEARCH_RESULTS = 50;

// Titles can be empty (custom line items have no variant title) and older API
// responses may still carry null: never call string methods on them unguarded.
function includesSearch(value: string | null | undefined, search: string) {
  return (value ?? "").toLowerCase().includes(search);
}

function matchesSearch(product: ProductAnalyticsItem, search: string) {
  return (
    includesSearch(product.title, search) ||
    (product.variants ?? []).some(
      (variant) =>
        includesSearch(variant.title, search) ||
        includesSearch(variant.sku, search),
    )
  );
}

type ProductVariantsListProps = {
  product: ProductAnalyticsItem;
  onSelectVariant: (variantId: string) => void;
};

function ProductVariantsList({
  product,
  onSelectVariant,
}: ProductVariantsListProps) {
  return (product.variants ?? []).map((variant) => (
    <ProductListItem
      key={variant.variantId}
      name={variant.title || product.title}
      subtitle={variant.title ? `(${product.title})` : undefined}
      image={product.image}
      count={variant.count}
      sku={variant.sku}
      onClick={() => onSelectVariant(variant.variantId)}
    />
  ));
}

type ProductListProps = {
  products: ProductAnalyticsItem[];
  onSelectProduct: (product: ProductAnalyticsItem) => void;
};

function ProductList({ products, onSelectProduct }: ProductListProps) {
  return products?.map((product) => (
    <ProductListItem
      key={product.id}
      name={product.title}
      image={product.image}
      count={product.count}
      onClick={() => onSelectProduct(product)}
    />
  ));
}

type ReturnRequestProductsListProps = {
  setProductFilter: (
    productId: string | undefined,
    variantId: string | undefined,
  ) => void;
};

export function ReturnRequestProductsList({
  setProductFilter,
}: ReturnRequestProductsListProps) {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 150);
  // Product clicked in the list, kept so the selection stays labelled if the
  // date range later excludes it.
  const [pickedProduct, setPickedProduct] =
    useState<ProductAnalyticsItem | null>(null);

  const {
    fromTimestamp,
    toTimestamp,
    productId,
    variantId,
    resolution,
    reason,
  } = useDashboardFilters();
  const { data: productsAnalytics } = useProductsSummaryAnalytics(shop.id, {
    fromTimestamp,
    toTimestamp: toTimestamp ?? 0,
    productId,
    variantId,
    resolution,
    reason,
  });

  // Same query key as the active filter badge in the dashboard header.
  const { data: recentProducts } = useAnalyticsProductsList({
    shopId: shop.id,
  });

  const timeRange = useFormattedTimeRange();

  // Selected product within the current filters: drives the variants list.
  const selectedProduct = productId
    ? (productsAnalytics ?? []).find((p) => p.id === productId)
    : undefined;

  // Label of the selection, even when the current filters exclude it.
  const selectionLabelProduct = productId
    ? (selectedProduct ??
      recentProducts?.find((p) => p.id === productId) ??
      (pickedProduct?.id === productId ? pickedProduct : null))
    : null;

  const searchTerm = debouncedSearch.trim().toLowerCase();

  // Derived from the current analytics response (not stored in state), so
  // results follow the date range and the other filters.
  const displayProducts = useMemo(() => {
    const products = productsAnalytics ?? [];
    if (!searchTerm) {
      return products;
    }
    return products
      .filter((product) => matchesSearch(product, searchTerm))
      .slice(0, MAX_SEARCH_RESULTS);
  }, [productsAnalytics, searchTerm]);

  const selectProduct = (
    nextProductId: string | undefined,
    nextVariantId: string | undefined,
  ) => {
    setSearch("");
    setProductFilter(nextProductId, nextVariantId);
  };

  const isEmpty =
    productsAnalytics !== undefined &&
    (productId ? !selectedProduct : displayProducts.length === 0);

  return (
    <Card className="col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-md">
          {t("analyticsDashboard.producstList.title")}
        </CardTitle>
        <CardDescription>{timeRange}</CardDescription>
      </CardHeader>
      <CardContent>
        <ProductSelectSearch
          search={search}
          onSearchChange={setSearch}
          onClearSelection={() => selectProduct(undefined, undefined)}
          selectedProduct={selectionLabelProduct}
        />
        <ScrollArea className="h-96 mt-4">
          <div className="flex flex-col gap-2">
            {isEmpty && (productId || searchTerm) ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t("productSelect.noResult")}
              </p>
            ) : selectedProduct ? (
              <ProductVariantsList
                product={selectedProduct}
                onSelectVariant={(variantId) =>
                  selectProduct(productId, variantId)
                }
              />
            ) : (
              <ProductList
                products={displayProducts}
                onSelectProduct={(product) => {
                  setPickedProduct(product);
                  selectProduct(product.id, undefined);
                }}
              />
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
